import * as React from "react";
import {
  Column,
  Columns,
  Divider,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { AccountPayer, FormValues } from "./types";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import ReadOnlyUploadedFile from "./FormComponents/ReadOnlyUploadedFile";
import {
  capitalizeFirstChar,
  determineMeterProvider,
  getInstallationTypeLabel,
  mapKeyToConnectionTypeLabel,
} from "./helper";
import RequestorInfo from "./RequestorInfo";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import FormContext from "src/context/FormContext";
import { useSiteSupervisorName } from "../Hooks/useSiteSupervisorName";
import ReadOnlyContact from "./FormComponents/ReadOnlyContact";

const ReadOnlyElectricityBlock = () => {
  const { values } = useFormikContext<FormValues>();
  const { formState } = React.useContext(FormContext);
  const { firstName, lastName, email, phone } = useSiteSupervisorName(
    values.siteSupervisor,
    values.requestingCompany?.msaNumber
  );
  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {/* Lodged request metadata */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* Account number */}
              {values.electricityAccountPayer === AccountPayer.COMPANY &&
                values.requestingCompany?.msaNumber && (
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Account number
                      </Text>
                      <Text>{values.requestingCompany.msaNumber}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                )}
              {/* End Account number */}
              {/* National metering identifier */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    National Metering Identifier
                  </Text>
                  <Text>{values.nmi ? values.nmi : "Pending"}</Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End National metering identifier */}
              {/* Allocate NMI Order Number */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Allocate NMI Order Number
                  </Text>
                  <Text>
                    {values.allocateNmiOrderNumber
                      ? values.allocateNmiOrderNumber
                      : "Pending"}
                  </Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Allocate NMI Order Number */}
              {/* Meter Service Order Number */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Meter Service Order Number
                  </Text>
                  <Text>
                    {values.meterServiceOrderNumber
                      ? values.meterServiceOrderNumber
                      : "Pending"}
                  </Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Meter Service Order Number */}
              {/* Supply Service Order Number */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Supply Service Order Number
                  </Text>
                  <Text>
                    {values.electricitySupplyServiceOrderNumber
                      ? values.electricitySupplyServiceOrderNumber
                      : "Pending"}
                  </Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Supply Service Order Number */}
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Lodged request metadata */}

        {/* Electrician + Connection type */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* Connection type */}
              <ReadOnlyFieldWrapper value={values.connectionType}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Connection type
                    </Text>
                    <Text>
                      {mapKeyToConnectionTypeLabel(values.connectionType!)}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Connection type */}
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Electrician + Connection type */}

        {/* Meter provider + ASP */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* Meter provider */}
              <ReadOnlyFieldWrapper
                value={
                  values.meterProvider ||
                  formState?.electricityService?.channel === "CLAIM"
                }
              >
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Meter provider
                    </Text>
                    <Text>
                      {formState?.electricityService?.channel === "CLAIM"
                        ? determineMeterProvider(
                            values.state!,
                            values.meterProvider
                          )
                        : values.meterProvider}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Meter provider */}
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Meter provider + ASP */}

        {/* Max load */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              <Columns
                collapseBelow="sm"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column>
                  {/* Max load, Amps */}
                  <ReadOnlyFieldWrapper value={values.maxLoad}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Max load, Amps
                        </Text>
                        <Text>{values.maxLoad}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Max load, Amps */}
                </Column>
                <Column />
              </Columns>
              {/* Current transformer required */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Current transformer required
                  </Text>
                  <Text>{values.transformer ? "True" : "False"}</Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Current transformer required */}
            </Stack>
          </Column>
          {/* End Fields */}
        </Columns>
        {/* End Max load */}

        {/* Consumption */}
        <ReadOnlyFieldWrapper value={values.moreThan100Consumption}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    More than 100 MW-h/year consumption expected
                  </Text>
                  <Text>{values.moreThan100Consumption}</Text>
                </Stack>
                <Divider />
              </Stack>
            </Column>
            {/* End Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Consumption */}
        {/* Truck appointment */}
        {formState?.electricityService?.channel !== "CLAIM" &&
          values.state === "VIC" && (
            <Columns
              collapseBelow="md"
              space={{
                xs: "none",
                md: "xlarge",
              }}
              alignY="stretch"
            >
              {/* Fields */}
              <Column width="2/3">
                <Stack
                  space={{
                    xs: "xlarge",
                    md: "xlarge",
                  }}
                >
                  {/* Truck appointment value */}
                  <ReadOnlyFieldWrapper value={values.truckAppointment}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Truck appointment or a REC (registered electrical
                          contractor) attendance required
                        </Text>
                        <Text>{values.truckAppointment ? "Yes" : "No"}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Truck appointment value */}
                  {/* Truck appointment reason */}
                  <ReadOnlyFieldWrapper value={values.truckAppointment}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Reason for truck appointment
                        </Text>
                        <Text>{values.truckAppointmentReason}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Truck appointment reason */}
                </Stack>
              </Column>
              {/* Fields */}
              {/* HelperText */}
              <Column width="1/3" />
              {/* End HelperText */}
            </Columns>
          )}
        {/* End Truck appointment */}
        {/* NMI */}
        <ReadOnlyFieldWrapper value={values.nmi}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}

            <Column width="2/3">
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    NMI
                  </Text>
                  <Text>{values.nmi}</Text>
                </Stack>
                <Divider />
              </Stack>
            </Column>

            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End NMI */}

        {/* NTC, Install type and phases */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* NTC (Network tariff code) */}
              <ReadOnlyFieldWrapper value={values.ntc}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      NTC (Network tariff code)
                    </Text>
                    <Text>{values.ntc}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End NTC (Network tariff code) */}

              <Columns
                collapseBelow="sm"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column width="2/3">
                  {/* Installation type */}
                  <ReadOnlyFieldWrapper value={values.installationType}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Installation type
                        </Text>
                        <Text>
                          {getInstallationTypeLabel(values.installationType)}
                        </Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Installation type */}
                </Column>
                <Column width="1/3">
                  {/* Phases */}
                  <ReadOnlyFieldWrapper value={values.phases}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Phases
                        </Text>
                        <Text>{values.phases}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Phases */}
                </Column>
              </Columns>
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End NTC, Install type and phases */}

        {/* Location description */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* Location description */}
              <ReadOnlyFieldWrapper value={values.locationDescription}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Location description
                    </Text>
                    <Text>
                      {capitalizeFirstChar(values.locationDescription)}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Location description */}
              {/* Other location description */}
              <ReadOnlyFieldWrapper value={values.otherLocationDescription}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Other location description
                    </Text>
                    <Text>{values.otherLocationDescription}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Other location description */}
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Location description */}

        {/* Solar */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              <ReadOnlyFieldWrapper value={values.solar}>
                {/* Solar install */}
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Will there be a solar system on site on the date of meter
                      installation?
                    </Text>
                    <Text>{capitalizeFirstChar(values.solar)}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Solar install */}
              <Columns
                collapseBelow="sm"
                space={{
                  xs: "none",
                  md: "medium",
                }}
              >
                <Column>
                  {/* Solar system size */}
                  <ReadOnlyFieldWrapper value={values.sizeOfSolarSystem}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Solar system size (kW)
                        </Text>
                        <Text>{values.sizeOfSolarSystem}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Solar system size */}
                </Column>
                <Column />
              </Columns>
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Solar */}

        {/* Off peak requirements */}
        <ReadOnlyFieldWrapper value={values.otherOffpeakRequirement}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "xlarge",
                  md: "xlarge",
                }}
              >
                {/* Off-peak requirements */}
                {values.offpeakRequirements &&
                  values.offpeakRequirements.length > 0 && (
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Off-peak requirements
                        </Text>
                        <Text>
                          {values.offpeakRequirements.map((it) =>
                            capitalizeFirstChar(it)
                          )}
                        </Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  )}
                {/* End Off-peak requirements */}
                {/* Specify other */}
                <ReadOnlyFieldWrapper value={values.otherOffpeakRequirement}>
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Specify other
                      </Text>
                      <Text>{values.otherOffpeakRequirement}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                </ReadOnlyFieldWrapper>
                {/* End Specify other */}
              </Stack>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Off peak requirements */}
        {/* Rex Id */}
        <ReadOnlyFieldWrapper value={values.rexId}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "xlarge",
                  md: "xlarge",
                }}
              >
                {/* Rex Id */}
                {values.state === "SA" && (
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Rex Id
                      </Text>
                      <Text>{values.rexId}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                )}
                {/* End Rex Id */}
              </Stack>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Rex Id */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}

          <Column width="2/3">
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  Life support required
                </Text>
                <Text>
                  {values.electricityLifeSupport === undefined
                    ? "N/A"
                    : values.electricityLifeSupport
                    ? "Yes"
                    : "No"}
                </Text>
              </Stack>
              <Divider />
            </Stack>
          </Column>

          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>

        {/* Contacts */}
        <ReadOnlyFieldWrapper value={values.licensedElectrician}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced electrician"
                firstName={values.licensedElectricianDetails?.firstName}
                lastName={values.licensedElectricianDetails?.lastName}
                email={values.licensedElectricianDetails?.email}
                phone={values.licensedElectricianDetails?.phoneNumber}
                licenceNumber={values.licensedElectricianDetails?.licenceNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.aspDetails?.licenceNumber}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Accredited service provider (ASP)"
                firstName={values.aspDetails?.firstName}
                lastName={values.aspDetails?.lastName}
                licenceNumber={values.aspDetails?.licenceNumber}
                type={"asp"}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.siteSupervisor}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Site supervisor"
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Contacts */}
        {/* Supporting docs */}
        <ReadOnlyFieldWrapper
          value={
            (values.coesFiles && values.coesFiles.length > 0) ||
            (values.mcoesFiles && values.mcoesFiles.length > 0) ||
            (values.ewrFiles && values.ewrFiles.length > 0)
          }
        >
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "large",
                  md: "xlarge",
                }}
              >
                {((values.coesFiles && values.coesFiles.length > 0) ||
                  (values.mcoesFiles && values.mcoesFiles.length > 0) ||
                  (values.ewrFiles && values.ewrFiles.length > 0)) && (
                  <Stack space="medium">
                    <Text variant="body-small" tone="neutralLight">
                      Supporting Documents
                    </Text>
                    {values.coesFiles?.map((attachment) => (
                      <ReadOnlyUploadedFile
                        key={attachment.attachmentId}
                        attachment={attachment}
                      />
                    ))}
                    {values.mcoesFiles?.map((attachment) => (
                      <ReadOnlyUploadedFile
                        key={attachment.attachmentId}
                        attachment={attachment}
                      />
                    ))}
                    {values.ewrFiles?.map((attachment) => (
                      <ReadOnlyUploadedFile
                        key={attachment.attachmentId}
                        attachment={attachment}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Supporting docs */}
        <ReadOnlyFieldWrapper value={values.electricityNetworkFormNumber}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Distributor request ID */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "xlarge",
                  md: "xlarge",
                }}
              >
                {(values.state === "QLD" || values.state === "VIC") && (
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Distributor request ID
                      </Text>
                      <Text>{values.electricityNetworkFormNumber}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                )}
              </Stack>
            </Column>
            {/* End Distributor request ID */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* Requestor */}
        <FormFieldLayout>
          <RequestorInfo
            requesterName={values.electricityRequesterName || ""}
            submittedDateTime={values.electricitySubmittedDateTime || ""}
          />
        </FormFieldLayout>
        {/* End Requestor */}
      </Stack>
      {/* End Form container */}

      {/* Actions */}
      {/* End Actions */}
    </Stack>
  );
};

export default ReadOnlyElectricityBlock;
